/* eslint-disable @typescript-eslint/camelcase */
import EVENTS from './trackerEvents/events.json';
import { accumulateSectionUi } from './section-ui-accumulator';
import { AnalyticPayload, AnalyticPayloadGaEvent, AnalyticUiEvent, AnalyticUiEventSchema } from './types';

const createStub = (stubName: string): (() => void) => {
  // eslint-disable-next-line no-console
  console.warn(`Tracker SDK for "${stubName}" is not available`);
  return (): void => { /* nothing */ };
};

const sendTracker = {
  snowplow: window.snowplow ?? createStub('snowplow'),
  ga: window.ga ?? createStub('ga'),
};

const processUiEventSectionValue = (value: string | HTMLElement | null | undefined): string | null => {
  if (!value) return null;

  if (value instanceof HTMLElement) {
    return accumulateSectionUi(value);
  }

  return value;
};

const sendAnalytics = (
  category: AnalyticPayload['category'],
  action: AnalyticPayload['action'],
  label: AnalyticPayload['label'],
  property: AnalyticPayload['property'],
  value: AnalyticPayload['value'],
  customProperties: AnalyticPayload['customProperties'] = {},
): void => {
  const originalTracker = {
    category,
    action,
    label,
    property,
    value,
  };

  const snowplowTracker = {
    ...originalTracker,
    ...(customProperties.snowplow ? customProperties.snowplow : {}),
  };
  if (customProperties.snowplow !== false) {
    const { selfDescribingEvent } = customProperties.snowplow || {};
    if (selfDescribingEvent) {
      sendTracker.snowplow(
        'trackSelfDescribingEvent',
        selfDescribingEvent,
      );
    } else {
      sendTracker.snowplow(
        'trackStructEvent',
        snowplowTracker.category || '',
        snowplowTracker.action || '',
        snowplowTracker.label || '',
        snowplowTracker.property || '',
        snowplowTracker.value,
      );
    }
  }

  if (customProperties.ga !== false) {
    const gaTracker = {
      ...originalTracker,
      ...(customProperties.ga ? customProperties.ga : {}),
    } as AnalyticPayloadGaEvent;
    sendTracker.ga(
      'send', 'event',
      gaTracker.category,
      gaTracker.action,
      gaTracker.label,
      gaTracker.value,
      gaTracker.fieldsObject,
    );
  }
};

export const getAnalyticPayload = (retriever: (event: typeof EVENTS.events) => AnalyticPayload, labelOrPatch?: AnalyticPayload | string): AnalyticPayload => {
  try {
    const event = retriever(EVENTS.events);
    const patch = (
      (typeof labelOrPatch === 'string')
        ? { label: labelOrPatch }
        : labelOrPatch
    );
    return {
      ...event,
      ...patch,
    };
  } catch (error) {
    // TODO: Log error
    // eslint-disable-next-line no-console
    console.error(error);
    return {};
  }
};

export default {
  /**
   * send analytic information to snowplow and ga
   * Receive either a tracker object as its first parameter
   */
  send(
    payloadOrCategory: AnalyticPayload | AnalyticPayload['category'],
    action?: AnalyticPayload['category'],
    label?: AnalyticPayload['category'],
    property?: AnalyticPayload['category'],
    value?: AnalyticPayload['value'],
    customProperties?: AnalyticPayload['customProperties'],
  ): void {
    if (typeof payloadOrCategory === 'object') {
      sendAnalytics(
        payloadOrCategory.category,
        payloadOrCategory.action,
        payloadOrCategory.label,
        payloadOrCategory.property,
        payloadOrCategory.value,
        payloadOrCategory.customProperties,
      );
    } else {
      sendAnalytics(payloadOrCategory, action, label, property, value, customProperties);
    }
  },

  /**
   * send ui_event to snowplow
   */
  trackUiEvent(data: AnalyticUiEvent): void {
    const uiEventData: AnalyticUiEventSchema = {
      action: data.action,
      section: processUiEventSectionValue(data.section),
      resource_uri: data.resourceUri,
      item_class: data.itemClass,
      label: data.label,
      value: data.value,
      order: data.order,
      block_reason: data.blockReason,
      parent_uri: (
        data.parentUri == null
          ? null
          : (
            Array.isArray(data.parentUri)
              ? data.parentUri
              : [data.parentUri]
          )
      ),
    };

    sendAnalytics(
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      {
        ga: false, // Prevent sending event to GA
        snowplow: {
          selfDescribingEvent: {
            schema: 'iglu:com.viddsee/ui_event/jsonschema/1-0-0',
            data: uiEventData,
          },
        },
      },
    );
  },
};
